import { usePageProps, withLayouts } from 'react-dx'
import { Trans } from '@lingui/react/macro'

import ArticleAnchor from './_components/ArticleAnchor'
import RelatedArticlesSidebar from './_components/RelatedArticlesSidebar'
import style from './index.module.scss'

import type { NextPage } from 'next'
import type { getArticleData } from './_service'

import Breadcrumb from '@/components/Breadcrumb'
import DefaultLayout from '@/pages/tad.ai/_layout'
import AntdLayout from '@/pages/tad.ai/_layout/layouts/AntdLayout'
import RootLayout from '@/pages/tad.ai/_layout/layouts/RootLayout'
import SiteSpecificContextLayout from '@/pages/tad.ai/_layout/layouts/SiteSpecificContextLayout'
import { useLocalePath } from '@/hooks/routes'


import { useOrigin } from '@/hooks/routes';
import { useMemo } from 'react';
import { cls } from 'tagged-classnames-free';
import Link from '@/components/Link';
interface IArticlePageProps {
  pageData: Awaited<ReturnType<typeof getArticleData>>
  locales?: string[]
}
export const Page: NextPage<IArticlePageProps> = (props) => {
  const { pageData } = props

  const [origin] = useOrigin()
  const localeUrl = useLocalePath()

  const { articlePageData, menuList, recommend, sidebarArticles } = pageData
  const titles = menuList.map((item) => {
    return {
      key: item,
      title: item,
    }
  })

  const categoryUrl = articlePageData.category?.attributes.url || ''
  const breadcrumbData = useMemo(() => {
    const result = [
      {
        name: `Home`,
        href: `${origin}${localeUrl}`,
      },
    ]
    if (articlePageData.category) {
      result.push({
        name: articlePageData.category.attributes.h1,
        href: `${origin}${localeUrl}${categoryUrl}`,
      })
    }
    result.push({
      name: articlePageData.h1,
      href: articlePageData.url,
    })

    return result
  }, [
    articlePageData.category,
    articlePageData.h1,
    articlePageData.url,
    categoryUrl,
    origin,
    localeUrl,
  ])

  const contentWidth =
    sidebarArticles?.length > 0 ? 'max-w-[1344px]' : 'max-w-[1088px]'
  return (
    <div className={`relative size-full pb-20 pt-1`}>
      <Breadcrumb
        data={breadcrumbData}
        className={cls`relative z-[1] mx-auto flex ${contentWidth} items-center px-4 opacity-75`}
      />
      <div
        className={cls`
          relative z-[1] mx-auto mt-8 
          flex gap-8 px-4 lg:mt-10
          ${contentWidth}
        `}
      >
        <ArticleAnchor items={titles} />
        <div className='max-w-[800px]'>
          <h1 className='mb-4 text-4xl font-semibold text-white md:mb-5 md:text-6xl'>
            {articlePageData.h1}
          </h1>
          {/* eslint-disable-next-line react-dom/no-dangerously-set-innerhtml */}
          <article
            className={style.article}
            dangerouslySetInnerHTML={{ __html: articlePageData.content }}
          />
          {recommend && recommend.length > 0 ? (
            <>
              <h2 className='mb-3 text-2xl font-bold text-white md:text-4xl lg:mb-5 lg:mt-12'>
                <Trans>Related Posts</Trans>
              </h2>
              <div className='grid grid-cols-1 gap-3 md:grid-cols-2 md:gap-4'>
                {recommend?.map((item) => (
                  <Link
                    href={item.url}
                    key={item.url}
                    className={cls`
                      bg-f-bg-layout
                      group inline-block
                      rounded-lg p-3
                      hover:cursor-pointer 
                      md:p-5
                    `}
                  >
                    <h3
                      className={cls`
                        group-hover:text-f-primary
                        hover:text-f-primary-hover 
                        text-sm 
                        font-semibold text-white md:text-base
                      `}
                    >
                      <span>{item.h1}</span>
                    </h3>
                    <p className='text-f-text-secondary mt-2 text-xs md:text-sm'>
                      {item.description}
                    </p>
                  </Link>
                ))}
              </div>
            </>
          ) : null}
        </div>
        <RelatedArticlesSidebar sidebarArticles={sidebarArticles} />
      </div>
    </div>
  )
}

const LayoutsPage = withLayouts(Page, [
  (props) => {
    return (
      <DefaultLayout
        {...props}
        minHScreen
        main={{
          className: cls`bg-f-bg-base`,
        }}
        locales={[]}
      />
    )
  },
  (props) => {
    const { pageData, locales } = usePageProps<IArticlePageProps>()

    return (
      <RootLayout
        locales={[]}
        {...props}
        seo={{
          title: pageData.articlePageData.title,
          description: pageData.articlePageData.description,
        }}
      />
    )
  },
  (props) => {
    return <SiteSpecificContextLayout {...props} />
  },
  AntdLayout,
])
export default LayoutsPage
